import type { AspectRatio } from '@mg/shared';
import { ratioParts } from '@mg/shared';
import { Select } from '../../components/ui/Select';
import { cn } from '../../lib/cn';

/**
 * Aspect-ratio picker: the shared {@link Select} with a proportional outline drawn beside each
 * ratio.
 *
 * This used to be a second, hand-rolled dropdown — a portalled listbox with its own positioning,
 * dismissal and keyboard handling. It existed only because its options need a graphic, which the
 * option-children API supports directly (labels are ReactNode). Keeping two implementations meant
 * fixing every bug twice: the adversarial review's Escape-closes-the-modal defect was present in
 * both, with the same incorrect rationale copied into both comments.
 */
export function AspectRatioSelect({
  value,
  options,
  onChange,
  disabled,
  id,
  onOpen,
}: {
  value: AspectRatio;
  /** Every ratio to show; those absent from `enabled` render greyed. */
  options: { ratio: AspectRatio; enabled: boolean; reason?: string }[];
  onChange: (ratio: AspectRatio) => void;
  disabled?: boolean;
  id?: string;
  /** Fired when the list opens — the modal uses it to close the Media Library overlay. */
  onOpen?: () => void;
}) {
  return (
    <Select
      id={id}
      value={value}
      disabled={disabled}
      onOpen={onOpen}
      onChange={(e) => onChange(e.target.value as AspectRatio)}
      className="h-10 w-full"
    >
      {options.map(({ ratio, enabled, reason }) => (
        <option
          key={ratio}
          value={ratio}
          disabled={!enabled}
          title={!enabled ? reason : undefined}
          // `label` is the plain-text form type-ahead uses; the children below are what renders.
          label={ratioLabel(ratio)}
        >
          {/*
            ONE inline-flex wrapper, and it has to be inline.

            Select renders an option's children inside a single `<span class="truncate">` — used for
            both the row and the closed trigger. That span is inline, so a block-level child breaks
            the line and the shape stacked above the ratio. `flex` is block-level; `inline-flex`
            lays the two out side by side and still flows inside the wrapper. Fixing it here rather
            than in Select keeps the shared component's contract ("children are ReactNode") intact
            for every other caller, whose labels are plain strings.
          */}
          <span className="inline-flex items-center gap-2 align-middle">
            <RatioShape ratio={ratio} muted={!enabled} />
            {ratioLabel(ratio)}
          </span>
        </option>
      ))}
    </Select>
  );
}

function RatioShape({ ratio, muted }: { ratio: AspectRatio; muted?: boolean }) {
  const parts = ratioParts(ratio);
  if (!parts) {
    return <span className="inline-block w-5 shrink-0 text-center text-[0.6875rem] text-ink-faint">—</span>;
  }
  // One box, scaled to fit the h-5 w-5 slot (its longer side 1.125rem, 18px at full size), with CSS
  // holding the proportion exactly. In rem, like the slot, so the two shrink together at 80%.
  const scale = 1.125 / Math.max(parts.w, parts.h);
  return (
    // inline-flex, not flex: this sits beside the ratio text inside an inline wrapper, and a
    // block-level box there pushes the text onto its own line.
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden>
      <span
        className={cn('block rounded-[0.125rem] border', muted ? 'border-line' : 'border-ink-faint')}
        style={{ width: `${parts.w * scale}rem`, height: `${parts.h * scale}rem` }}
      />
    </span>
  );
}

export function ratioLabel(ratio: AspectRatio): string {
  return ratio === 'auto' ? 'Auto Ratio' : ratio;
}
